import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Icon from '@msi/cobalt-react/icon';
import { useOktaAuth } from '@okta/okta-react';
import ReleaseNotesService, { ReleaseNoteResponse } from '../../services/releaseNotesService';
import './ReleaseNotes.css';

const ReleaseNotes: React.FC = () => {
  const { articleId } = useParams<{ articleId?: string }>();
  const navigate = useNavigate();
  const { authState } = useOktaAuth();
  const releaseNotesService = useMemo(() => new ReleaseNotesService(), []);

  const [releases, setReleases] = useState<ReleaseNoteResponse[]>([]);
  const [selectedRelease, setSelectedRelease] = useState<ReleaseNoteResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!authState?.isAuthenticated) return;

    releaseNotesService.getReleases(authState)
      .then(data => {
        setReleases(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load release notes.');
        setLoading(false);
      });
  }, [authState?.isAuthenticated, releaseNotesService]);

  useEffect(() => {
    if (articleId) {
      const found = releases.find(r => String(r.id) === articleId);
      setSelectedRelease(found || releases[0] || null);
    } else {
      setSelectedRelease(releases[0] || null);
    }
  }, [articleId, releases]);

  const handleSelectRelease = (release: ReleaseNoteResponse) => {
    setSelectedRelease(release);
    navigate(`/release-notes/article/${release.id}`, { replace: true });
  };

  const handleGoBack = () => {
    navigate('/');
  };

  const formatDate = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="release-notes-page">
      <div className="release-notes-header">
        <div className="release-notes-back-btn" onClick={handleGoBack}>
          <Icon name="ic_back" size={36} color="primary" />
        </div>
        <h1>Release notes</h1>
      </div>

      <div className="release-notes-layout">
        <aside className="release-notes-sidebar">
          {releases.map(release => (
            <button
              key={release.id}
              className={`release-notes-version-btn ${selectedRelease?.id === release.id ? 'active' : ''}`}
              onClick={() => handleSelectRelease(release)}
            >
              <span className="version-name">{release.name}</span>
              <span className="version-date">{formatDate(release.publishedAt)}</span>
            </button>
          ))}
        </aside>

        <main className="release-notes-content-area">
          {loading ? (
            <p>Loading release notes...</p>
          ) : error ? (
            <p>{error}</p>
          ) : selectedRelease ? (
            <ReactMarkdown>{selectedRelease.body}</ReactMarkdown>
          ) : (
            <p>No release notes available.</p>
          )}
        </main>
      </div>
    </div>
  );
};

export default ReleaseNotes;
